import Debug from "debug";
import { UserDetailsApiModel } from "../../api-models";
import { settleAll } from "../utilities/settle-all";
import { AnalyticsProvider } from "./analytics-provider";
import { MixpanelAnalytics } from "./mixpanel-analytics";
import { FeatureName } from "./analytics-features";

const debug = Debug("cs.Analytics");

class AllAnalytics {
  private configuredProviders: AnalyticsProvider[] | undefined;

  private get providers() {
    if (!this.configuredProviders) {
      this.configuredProviders = [new MixpanelAnalytics()];
    }
    return this.configuredProviders;
  }

  public suspendAnalytics = false;

  private isIdentified = false;

  get enabledProviders() {
    // We don't record any analytics until the user has been identified,
    // either by login, or during the offline loading process
    return this.isIdentified && !this.suspendAnalytics
      ? this.providers.filter((p) => p.enabled)
      : [];
  }

  identify(userDetails: UserDetailsApiModel) {
    debug("Identify");
    if (!!userDetails && !!userDetails.id) {
      this.isIdentified = true;
      this.enabledProviders.forEach((p) => p.identify(userDetails.id));
    }
  }

  action(actionName: string, featureName: FeatureName, extraData?: any) {
    debug(`Action: ${actionName}`);
    this.enabledProviders.forEach((p) => p.action(actionName, featureName, extraData));
  }

  actionAsync(actionName: string, featureName: FeatureName, extraData?: any): Promise<void> {
    debug(`ActionAsync: ${actionName}`);
    return new Promise<void>((resolve, reject) => {
      const promises: Promise<void>[] = [];
      this.enabledProviders.forEach((p) => {
        const promise = p.actionAsync(actionName, featureName, extraData);
        promises.push(promise);
      });
      settleAll(promises).then((results) => {
        if (results.every((r) => r.isFulfilled)) {
          resolve();
        } else {
          reject();
        }
      });
    });
  }

  event(eventName: string, extraData?: any) {
    debug(`Event: ${eventName}`);
    this.enabledProviders.forEach((p) => p.event(eventName, extraData));
  }

  screen(screenName: string) {
    debug(`Screen: ${screenName}`);
    this.enabledProviders.forEach((p) => p.screen(screenName));
  }

  async logout(): Promise<void> {
    debug("logout");
    const providersAndPromises = this.enabledProviders.map((p) => {
      return { name: p.name, logoutPromise: p.logout() };
    });
    const results = await settleAll(providersAndPromises.map((p) => p.logoutPromise));
    this.isIdentified = false;
    if (results.some((r) => !r.isFulfilled)) {
      throw new Error("One or more analytics providers failed to logout: ");
    }
  }
}

// eslint-disable-next-line import/prefer-default-export
export const Analytics = new AllAnalytics();
